package top.wcpe.allincore.ui.runtime.ir.prop

import top.wcpe.allincore.ui.runtime.element.Divider
import top.wcpe.allincore.ui.runtime.element.Dropdown
import top.wcpe.allincore.ui.runtime.element.Image
import top.wcpe.allincore.ui.runtime.element.ItemStack
import top.wcpe.allincore.ui.runtime.element.RadioGroup
import top.wcpe.allincore.ui.runtime.element.Splitter
import top.wcpe.allincore.ui.runtime.element.Tab
import top.wcpe.allincore.ui.runtime.element.TabView
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.ir.eval.ValueCoercion
import top.wcpe.allincore.ui.runtime.render.model.ImageStyleMode
import kotlin.jvm.JvmStatic

/**
 * 结构控件属性贡献者：selectedValue / activeIndex / title / thickness / itemId / count /
 * resource。
 *
 * 多为只写属性，不参与动画起点读取。
 */
@Suppress(
    "CyclomaticComplexMethod",
    "LongMethod",
    "TooManyFunctions",
    "ComplexCondition",
    "LoopWithTooManyJumpStatements",
)
internal object StructurePropContributor {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.prop.StructurePropContributor")

    @JvmStatic
    fun contribute(m: MutableMap<String, PropBinding>) {
        m.put(
            "resource",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is Image) {
                    el.resource(v.toString())
                } else {
                    PropDiag.unsupported(LOGGER, el, "resource")
                }
            },
        )

        // image 的 UV 切片：uvX/uvY/uvW/uvH，均为像素坐标，仅 Image 接受
        m.put(
            "uvX",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is Image) {
                    el.uvX(ValueCoercion.toFloat(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "uvX")
                }
            },
        )
        m.put(
            "uvY",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is Image) {
                    el.uvY(ValueCoercion.toFloat(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "uvY")
                }
            },
        )
        m.put(
            "uvW",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is Image) {
                    el.uvW(ValueCoercion.toFloat(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "uvW")
                }
            },
        )
        m.put(
            "uvH",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is Image) {
                    el.uvH(ValueCoercion.toFloat(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "uvH")
                }
            },
        )

        m.put(
            "selectedValue",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                when (el) {
                    is RadioGroup -> el.selectedValue(v.toString())
                    is Dropdown -> el.selectedValue(v.toString())
                    else -> PropDiag.unsupported(LOGGER, el, "selectedValue")
                }
            },
        )

        // wiki dropdown 常用 options = ["a","b"]；与 items 同语义，走 rebuildOptions。
        m.put(
            "options",
            PropBinding.writeOnly { el, v ->
                if (el !is Dropdown) {
                    PropDiag.unsupported(LOGGER, el, "options")
                    return@writeOnly
                }
                el.rebuildOptions(optionsFromRaw(v))
            },
        )

        m.put(
            "activeIndex",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is TabView) {
                    el.activeIndex(ValueCoercion.toFloat(v).toInt())
                } else {
                    PropDiag.unsupported(LOGGER, el, "activeIndex")
                }
            },
        )

        // v4 A.1：contextMenuTriggerMs —— 写入 UIElement 基类字段，元素类型不限；
        // null 写入哨兵 0L 表示"未声明"，平台层回退到主题 longPressThresholdMs。
        m.put(
            "contextMenuTriggerMs",
            PropBinding.writeOnly { el, v ->
                if (v == null) {
                    el.contextMenuTriggerMs(UIElement.CONTEXT_MENU_TRIGGER_MS_UNSET)
                    return@writeOnly
                }
                val ms = ValueCoercion.toFloat(v).toLong()
                el.contextMenuTriggerMs(ms)
            },
        )

        m.put(
            "title",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is Tab) {
                    el.title(v.toString())
                } else {
                    PropDiag.unsupported(LOGGER, el, "title")
                }
            },
        )

        m.put(
            "thickness",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is Divider) {
                    el.thickness(ValueCoercion.toFloat(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "thickness")
                }
            },
        )

        m.put(
            "itemId",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is ItemStack) {
                    el.itemId(v.toString())
                } else {
                    PropDiag.unsupported(LOGGER, el, "itemId")
                }
            },
        )

        m.put(
            "count",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is ItemStack) {
                    el.count(ValueCoercion.toFloat(v).toInt())
                } else {
                    PropDiag.unsupported(LOGGER, el, "count")
                }
            },
        )

        m.put(
            "durability",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is ItemStack) {
                    el.durability(ValueCoercion.toFloat(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "durability")
                }
            },
        )

        m.put(
            "enchanted",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is ItemStack) {
                    el.enchanted(ValueCoercion.toBool(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "enchanted")
                }
            },
        )

        m.put(
            "cooldown",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is ItemStack) {
                    el.cooldown(ValueCoercion.toFloat(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "cooldown")
                }
            },
        )

        m.put(
            "displayName",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is ItemStack) {
                    el.displayName(v.toString())
                } else {
                    PropDiag.unsupported(LOGGER, el, "displayName")
                }
            },
        )

        m.put(
            "lore",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el !is ItemStack) {
                    PropDiag.unsupported(LOGGER, el, "lore")
                    return@writeOnly
                }
                if (v !is List<*>) {
                    LOGGER.warn("lore 期望 List 类型，实际为 ${v::class.simpleName}")
                    return@writeOnly
                }
                val lines = v.map { it?.toString() ?: "" }
                el.lore(lines)
            },
        )

        m.put(
            "customNbt",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el !is ItemStack) {
                    PropDiag.unsupported(LOGGER, el, "customNbt")
                    return@writeOnly
                }
                if (v !is Map<*, *>) {
                    LOGGER.warn("customNbt 期望 Map 类型，实际为 ${v::class.simpleName}")
                    return@writeOnly
                }
                val nbt = linkedMapOf<String, Any>()
                for ((key, value) in v) {
                    if (key == null) continue
                    nbt[key.toString()] = value ?: continue
                }
                el.customNbt(nbt)
            },
        )

        // M7.5 IMAGE_STYLED：image 元素的 UV 变换属性
        m.put(
            "uvRotation",
            PropBinding.of(
                { el, v -> if (v != null && el is Image) el.uvRotation(ValueCoercion.toFloat(v)) },
                { el -> if (el is Image) el.uvRotation().toDouble() else null },
            ),
        )
        m.put(
            "uvScaleX",
            PropBinding.of(
                { el, v -> if (v != null && el is Image) el.uvScaleX(ValueCoercion.toFloat(v)) },
                { el -> if (el is Image) el.uvScaleX().toDouble() else null },
            ),
        )
        m.put(
            "uvScaleY",
            PropBinding.of(
                { el, v -> if (v != null && el is Image) el.uvScaleY(ValueCoercion.toFloat(v)) },
                { el -> if (el is Image) el.uvScaleY().toDouble() else null },
            ),
        )
        m.put(
            "uvOffsetX",
            PropBinding.of(
                { el, v -> if (v != null && el is Image) el.uvOffsetX(ValueCoercion.toFloat(v)) },
                { el -> if (el is Image) el.uvOffsetX().toDouble() else null },
            ),
        )
        m.put(
            "uvOffsetY",
            PropBinding.of(
                { el, v -> if (v != null && el is Image) el.uvOffsetY(ValueCoercion.toFloat(v)) },
                { el -> if (el is Image) el.uvOffsetY().toDouble() else null },
            ),
        )
        m.put(
            "imageMode",
            PropBinding.writeOnly { el, v ->
                if (v == null || el !is Image) return@writeOnly
                val name = v.toString().trim().uppercase()
                try {
                    el.imageMode(ImageStyleMode.valueOf(name))
                } catch (e: IllegalArgumentException) {
                    LOGGER.warn("非法 imageMode: $v")
                }
            },
        )

        // Task 2c-E1：splitter 的比例 / 范围 / 方向 / 总长属性，仅 Splitter 接受。
        // ratio = state.x 双向绑定走 IrInterpreter.bindStateBackedControl；这里处理字面量 / 表达式写入。
        // 注意 toFloat(v).toDouble()：ratio/minRatio/maxRatio setter 形参为 Double，但须忠实复现原 Java
        // 的 ratio(toFloat(v)) —— float 经隐式拓宽入 double 的取值（含 float 精度）。直接 toDouble 会得到不同数值。
        m.put(
            "ratio",
            PropBinding.of(
                { el, v -> if (v != null && el is Splitter) el.ratio(ValueCoercion.toFloat(v).toDouble()) },
                { el -> if (el is Splitter) el.ratio() else null },
            ),
        )
        m.put(
            "minRatio",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is Splitter) {
                    el.minRatio(ValueCoercion.toFloat(v).toDouble())
                } else {
                    PropDiag.unsupported(LOGGER, el, "minRatio")
                }
            },
        )
        m.put(
            "maxRatio",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is Splitter) {
                    el.maxRatio(ValueCoercion.toFloat(v).toDouble())
                } else {
                    PropDiag.unsupported(LOGGER, el, "maxRatio")
                }
            },
        )
        m.put(
            "orientation",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is Splitter) {
                    el.orientation(v.toString())
                } else {
                    PropDiag.unsupported(LOGGER, el, "orientation")
                }
            },
        )
        m.put(
            "total",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is Splitter) {
                    el.total(ValueCoercion.toFloat(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "total")
                }
            },
        )
    }

    private fun optionsFromRaw(raw: Any?): List<top.wcpe.allincore.ui.runtime.element.DropdownOption> {
        val result = ArrayList<top.wcpe.allincore.ui.runtime.element.DropdownOption>()
        if (raw is List<*>) {
            for (item in raw) {
                if (item is Map<*, *>) {
                    val value = item["value"]?.toString() ?: ""
                    val label = item["label"]?.toString() ?: value
                    val opt = top.wcpe.allincore.ui.runtime.element.DropdownOption.create()
                    opt.value(value)
                    opt.label(label)
                    result.add(opt)
                } else {
                    val v = item?.toString() ?: ""
                    val opt = top.wcpe.allincore.ui.runtime.element.DropdownOption.create()
                    opt.value(v)
                    opt.label(v)
                    result.add(opt)
                }
            }
        } else if (raw != null) {
            val v = raw.toString()
            val opt = top.wcpe.allincore.ui.runtime.element.DropdownOption.create()
            opt.value(v)
            opt.label(v)
            result.add(opt)
        }
        return result
    }
}
